import React, { useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Radio, Popconfirm, Typography, Alert, message, Empty } from 'antd';
import {
  ArrowLeft, Pencil, Trash2, ArrowRight, CheckCircle2, Star, Target, Briefcase, TrendingUp, Activity,
  Award, Trophy, Calendar, Plus, Check, Paperclip, History, PartyPopper,
} from 'lucide-react';
import dayjs from 'dayjs';
import {
  CRITERION_STATUS, careerApi, currentRole, doneCount, isCompleted, isReady, progressOf, stepState, targetRole,
  type AchievementType, type CriterionStatus, type Roadmap,
} from '../careerStore';

const STEP_LABEL = { completed: 'Đã đạt', current: 'Hiện tại', target: 'Mục tiêu', future: 'Tương lai' };
const ACH_TYPE: Record<AchievementType, { label: string; icon: typeof Trophy; cls: string; glow: string }> = {
  award: { label: 'Khen thưởng', icon: Trophy, cls: 'bg-yellow-50 dark:bg-yellow-950/30 text-yellow-600 dark:text-yellow-400 border border-yellow-100 dark:border-yellow-900', glow: 'bg-yellow-400' },
  certificate: { label: 'Chứng chỉ', icon: Award, cls: 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-900', glow: 'bg-rose-400' },
  project: { label: 'Thành tích', icon: Star, cls: 'bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400 border border-purple-100 dark:border-purple-900', glow: 'bg-purple-400' },
};
const CRIT_CLS: Record<CriterionStatus, { dot: string; card: string; badge: string; value: string }> = {
  done: { dot: 'border-green-500 bg-green-500', card: 'bg-green-50/30 dark:bg-green-950/20 border-green-100 dark:border-green-900/50', badge: 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300', value: 'text-green-600' },
  doing: { dot: 'border-rose-400 bg-white dark:bg-gray-900', card: 'bg-rose-50/30 dark:bg-rose-950/20 border-rose-100 dark:border-rose-900/50', badge: 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300', value: 'text-rose-600' },
  todo: { dot: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900', card: 'bg-gray-50/50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-700', badge: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300', value: 'text-gray-500' },
};

/** Chi tiết một lộ trình: bản đồ thăng tiến, điều kiện, thành tựu, đánh giá */
export default function ViewComponent({ roadmap: r, evaluator, onBack, onEdit, onDeleted }: {
  roadmap: Roadmap; evaluator: string; onBack: () => void; onEdit: () => void; onDeleted: () => void;
}) {
  const [achOpen, setAchOpen] = useState(false);
  const [evalOpen, setEvalOpen] = useState(false);
  const [achForm] = Form.useForm();
  const [evalForm] = Form.useForm();
  const progress = progressOf(r);
  const target = targetRole(r);
  const completed = isCompleted(r);
  const ready = isReady(r);
  const n = r.steps.length;
  // vạch tiến trình: tới bậc hiện tại + phần đã đi được tới mục tiêu
  const trackPct = n > 1 ? Math.min(100, ((r.currentIndex + (completed ? 0 : progress / 100)) / (n - 1)) * 100) : 100;

  const remove = () => Modal.confirm({
    title: `Xoá lộ trình “${r.name}”?`, content: 'Toàn bộ điều kiện, thành tựu và lịch sử đánh giá sẽ bị xoá.',
    okText: 'Xoá', okButtonProps: { danger: true }, cancelText: 'Hủy',
    onOk: () => { careerApi.remove(r.id); message.success('Đã xoá lộ trình'); onDeleted(); },
  });

  const submitAchievement = (v: { title: string; date: dayjs.Dayjs; type: AchievementType; description?: string }) => {
    careerApi.addAchievement(r.id, { title: v.title.trim(), date: v.date.format('YYYY-MM'), type: v.type, description: (v.description || '').trim() });
    message.success('Đã ghi nhận thành tựu');
    setAchOpen(false);
    achForm.resetFields();
  };

  const submitEvaluation = (v: { result: 'promoted' | 'continue'; note: string }) => {
    careerApi.evaluate(r.id, v.result, v.note, evaluator);
    setEvalOpen(false);
    evalForm.resetFields();
    if (v.result === 'promoted') {
      Modal.success({
        title: `Chúc mừng ${r.employee} đã lên ${target}!`,
        content: targetRole({ ...r, currentIndex: r.currentIndex + 1 })
          ? 'Các điều kiện đã được đặt lại về “Chưa đạt”. Hãy cập nhật điều kiện cho mục tiêu tiếp theo.'
          : 'Nhân sự đã hoàn thành toàn bộ lộ trình.',
        okText: targetRole({ ...r, currentIndex: r.currentIndex + 1 }) ? 'Cập nhật điều kiện' : 'Đóng',
        onOk: () => { if (targetRole({ ...r, currentIndex: r.currentIndex + 1 })) onEdit(); },
      });
    } else message.success('Đã lưu kết quả đánh giá');
  };

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] dark:bg-gray-950 min-w-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0 w-full md:w-auto">
          <button onClick={onBack} title="Về danh sách lộ trình" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 cursor-pointer shrink-0"><ArrowLeft size={18} /></button>
          <div className="w-11 h-11 rounded-full text-white flex items-center justify-center font-bold text-lg shadow-md ring-4 ring-rose-50 dark:ring-rose-950/40 shrink-0"
            style={{ background: 'linear-gradient(to bottom right, #f472b6, #e11d48)' }}>
            {r.employee.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight truncate">{r.employee}</h2>
            <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5 min-w-0">
              <Star size={12} className="text-yellow-500 fill-yellow-500 shrink-0" />
              <span className="truncate">{currentRole(r)} • {r.department}</span>
            </p>
          </div>
        </div>
        <div className="flex gap-2 w-full md:w-auto justify-end">
          <button onClick={onEdit} className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1.5 cursor-pointer"><Pencil size={15} /> Sửa</button>
          <button onClick={remove} className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-1.5 cursor-pointer"><Trash2 size={15} /> Xoá</button>
          <button onClick={() => setEvalOpen(true)} disabled={completed}
            title={completed ? 'Lộ trình đã hoàn thành' : 'Đánh giá thăng tiến'}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm cursor-pointer">
            <ArrowRight size={15} /> Đánh giá
          </button>
        </div>
      </div>

      <div className="flex-1 p-4 sm:p-6 space-y-6">
        <div>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">{r.name}</h1>
          {r.targetMonth && !completed && (
            <p className={`text-xs mt-0.5 ${dayjs(`${r.targetMonth}-01`).endOf('month').isBefore(dayjs()) ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
              Hạn đạt mục tiêu: {dayjs(`${r.targetMonth}-01`).format('MM/YYYY')}{dayjs(`${r.targetMonth}-01`).endOf('month').isBefore(dayjs()) ? ' — đã quá hạn' : ''}
            </p>
          )}
        </div>

        {ready && (
          <Alert type="success" showIcon icon={<PartyPopper size={18} />}
            message={`${r.employee} đã đạt đủ ${r.criteria.length}/${r.criteria.length} điều kiện để lên ${target}`}
            action={<button onClick={() => setEvalOpen(true)} className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-bold hover:bg-green-700 cursor-pointer">Đánh giá ngay</button>} />
        )}
        {completed && <Alert type="info" showIcon message={`Đã hoàn thành lộ trình — vị trí hiện tại: ${currentRole(r)}`} description="Có thể thêm bậc tiếp theo trong phần Sửa để tiếp tục lộ trình." />}

        {/* Bản đồ thăng tiến + tiến độ */}
        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm flex flex-col xl:flex-row items-center gap-6">
          <div className="flex-1 w-full min-w-0">
            <h3 className="text-xs sm:text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 flex items-center"><TrendingUp size={16} className="mr-2" /> Bản đồ thăng tiến sự nghiệp</h3>
            <div className="overflow-x-auto pb-2">
              <div className="flex items-start justify-between relative" style={{ minWidth: n * 112 }}>
                <div className="absolute left-[56px] right-[56px] top-[22px] h-1">
                  <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  <div className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-green-400 via-rose-500 to-rose-300 rounded-full transition-all duration-700" style={{ width: `${trackPct}%` }} />
                </div>
                {r.steps.map((role, i) => {
                  const st = stepState(r, i);
                  return (
                    <div key={`${role}-${i}`} className="relative flex flex-col items-center w-28 text-center">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-sm ${st === 'completed' ? 'bg-green-50 dark:bg-green-950/30 border-2 border-green-200 dark:border-green-800 text-green-600'
                        : st === 'current' ? 'bg-white dark:bg-gray-900 border-2 border-rose-500 text-rose-600 ring-4 ring-rose-50 dark:ring-rose-950/40'
                          : st === 'target' ? 'bg-rose-50 dark:bg-rose-950/30 border-2 border-dashed border-rose-400 text-rose-500'
                            : 'bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-400'}`}>
                        {st === 'completed' && <CheckCircle2 size={22} />}
                        {st === 'current' && <Star size={24} className="fill-rose-100" />}
                        {st === 'target' && <Target size={24} />}
                        {st === 'future' && <Briefcase size={22} />}
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider mb-1 px-2 py-0.5 rounded-full ${st === 'completed' ? 'text-green-600 bg-green-50 dark:bg-green-950/30' : st === 'current' ? 'text-rose-600 bg-rose-50 dark:bg-rose-950/30' : st === 'target' ? 'text-rose-500' : 'text-gray-400'}`}>{STEP_LABEL[st]}</span>
                      <span className={`font-bold leading-tight ${st === 'current' || st === 'target' ? 'text-gray-900 dark:text-white text-sm' : 'text-gray-600 dark:text-gray-300 text-xs'}`}>{role}</span>
                      <span className="text-[10px] text-gray-400 mt-1 font-medium">{st === 'target' && r.targetMonth ? `Mục tiêu ${dayjs(`${r.targetMonth}-01`).format('MM/YYYY')}` : r.stepNotes[i] || ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="shrink-0 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-800/60 rounded-2xl border border-gray-100 dark:border-gray-700 w-full xl:w-48">
            <div className="relative w-28 h-28 flex items-center justify-center">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                <path className="text-gray-200 dark:text-gray-700" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path className={`${completed ? 'text-green-500' : 'text-rose-500'} transition-all duration-700`} strokeDasharray={`${completed ? 100 : progress}, 100`} strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              </svg>
              <span className="absolute text-2xl font-black text-gray-800 dark:text-white">{completed ? '100' : progress}%</span>
            </div>
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-3 text-center">{completed ? 'Đã hoàn thành lộ trình' : `Tiến độ lên ${target}`}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
          {/* Điều kiện */}
          <div className="2xl:col-span-2 space-y-3 min-w-0">
            <div className="flex justify-between items-center gap-2">
              <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 flex items-center"><Activity className="mr-2 text-rose-500 shrink-0" size={20} /> Điều kiện thăng tiến{target ? ` lên ${target}` : ''}</h3>
              <span className="bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 text-xs py-1 px-3 rounded-full font-bold border border-rose-100 dark:border-rose-900 shrink-0">Đạt {doneCount(r)} / {r.criteria.length}</span>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-4 sm:p-6 shadow-sm">
              {r.criteria.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có điều kiện — bấm Sửa để thêm" /> : (
                <div className="relative border-l-2 border-gray-100 dark:border-gray-800 ml-2 sm:ml-4 space-y-5">
                  {r.criteria.map(c => {
                    const cls = CRIT_CLS[c.status];
                    return (
                      <div key={c.id} className="relative pl-6 sm:pl-8">
                        <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${cls.dot}`}>
                          {c.status === 'done' && <Check size={10} className="text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 stroke-[3]" />}
                        </div>
                        <div className={`p-4 rounded-xl border ${cls.card}`}>
                          <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-2">
                            <h4 className="font-bold text-sm leading-tight text-gray-900 dark:text-white">{c.title}</h4>
                            <Select size="small" value={c.status} disabled={completed}
                              onChange={v => careerApi.setCriterion(r.id, c.id, { status: v })}
                              options={Object.entries(CRITERION_STATUS).map(([value, s]) => ({ value, label: s.label }))}
                              className={`min-w-[140px] [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!font-bold [&_.ant-select-selector]:!text-xs ${cls.badge} rounded-md`} />
                          </div>
                          <div className="flex flex-col lg:flex-row lg:items-center bg-white dark:bg-gray-800/70 rounded-lg border border-gray-100 dark:border-gray-700 p-3 gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">Mục tiêu yêu cầu</p>
                              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium flex items-center"><Target size={14} className="mr-1.5 text-gray-400 shrink-0" />{c.required}</p>
                            </div>
                            <div className="hidden lg:block w-px h-8 bg-gray-100 dark:bg-gray-700" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">Kết quả hiện tại</p>
                              <Typography.Text
                                className={`!text-sm !font-bold ${cls.value}`}
                                editable={completed ? false : {
                                  tooltip: 'Cập nhật kết quả', maxLength: 120,
                                  onChange: v => { if (v.trim() !== c.current) careerApi.setCriterion(r.id, c.id, { current: v.trim() }); },
                                }}
                              >
                                {c.current || <span className="text-gray-400 font-normal italic">Chưa cập nhật</span>}
                              </Typography.Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Lịch sử đánh giá */}
            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 flex items-center pt-2"><History className="mr-2 text-[#3e91ab]" size={20} /> Lịch sử đánh giá</h3>
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm">
              {r.evaluations.length === 0 ? <p className="text-sm text-gray-400 text-center py-3">Chưa có lần đánh giá nào.</p> : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {r.evaluations.map(e => (
                    <div key={e.id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${e.result === 'promoted' ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'}`}>
                          {e.result === 'promoted' ? `Thăng tiến: ${e.fromRole} → ${e.toRole}` : 'Tiếp tục theo dõi'}
                        </span>
                        <span className="text-xs text-gray-400">{dayjs(e.date).format('DD/MM/YYYY HH:mm')} · {e.by}</span>
                      </div>
                      {e.note && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5">{e.note}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Thành tựu */}
          <div className="space-y-3 min-w-0">
            <h3 className="text-base font-bold flex items-center"><Award className="mr-2 text-yellow-500 shrink-0" size={20} /> <span className="text-gray-800 dark:text-gray-100">Dấu ấn cá nhân</span></h3>
            <div className="space-y-3">
              {r.achievements.map(a => {
                const t = ACH_TYPE[a.type];
                return (
                  <div key={a.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-5 shadow-sm relative overflow-hidden group">
                    <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full blur-2xl opacity-40 ${t.glow}`} />
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`p-2.5 rounded-xl ${t.cls}`}><t.icon size={20} /></div>
                        <div className="flex items-center gap-1">
                          <span className="flex items-center text-[10px] font-bold text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-md border border-gray-100 dark:border-gray-700"><Calendar size={12} className="mr-1" />Tháng {dayjs(`${a.date}-01`).format('M/YYYY')}</span>
                          <Popconfirm title="Xoá thành tựu này?" okText="Xoá" cancelText="Hủy" okButtonProps={{ danger: true }} onConfirm={() => careerApi.removeAchievement(r.id, a.id)}>
                            <button title="Xoá" className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer"><Trash2 size={14} /></button>
                          </Popconfirm>
                        </div>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug mb-1.5">{a.title}</h4>
                      {a.description && <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{a.description}</p>}
                    </div>
                  </div>
                );
              })}
              <button onClick={() => setAchOpen(true)}
                className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-rose-600 hover:border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition flex items-center justify-center cursor-pointer">
                <Plus size={16} className="mr-2" /> Ghi nhận thành tựu mới
              </button>
            </div>

            {(r.note || r.attachments.length > 0) && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 shadow-sm space-y-2">
                {r.note && <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{r.note}</p>}
                {r.attachments.map(f => <div key={f} className="flex items-center gap-1.5 text-xs text-gray-500"><Paperclip size={12} /> {f}</div>)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Thêm thành tựu */}
      <Modal open={achOpen} title="Ghi nhận thành tựu mới" okText="Lưu" cancelText="Hủy" onCancel={() => setAchOpen(false)} onOk={() => achForm.submit()} destroyOnHidden>
        <Form form={achForm} layout="vertical" onFinish={submitAchievement} initialValues={{ type: 'award', date: dayjs() }} preserve={false}>
          <Form.Item name="title" label="Tên thành tựu" rules={[{ required: true, whitespace: true, message: 'Nhập tên thành tựu' }]}><Input maxLength={120} placeholder="VD: Nhân viên xuất sắc quý 3" /></Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="type" label="Loại"><Select options={Object.entries(ACH_TYPE).map(([value, t]) => ({ value, label: t.label }))} /></Form.Item>
            <Form.Item name="date" label="Thời gian" rules={[{ required: true, message: 'Chọn tháng' }]}><DatePicker picker="month" format="MM/YYYY" className="w-full" disabledDate={d => d.isAfter(dayjs(), 'month')} /></Form.Item>
          </div>
          <Form.Item name="description" label="Mô tả"><Input.TextArea rows={3} maxLength={300} showCount /></Form.Item>
        </Form>
      </Modal>

      {/* Đánh giá thăng tiến */}
      <Modal open={evalOpen} title={`Đánh giá thăng tiến: ${currentRole(r)} → ${target ?? ''}`} okText="Lưu kết quả" cancelText="Hủy"
        onCancel={() => setEvalOpen(false)} onOk={() => evalForm.submit()} destroyOnHidden>
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
          Đã đạt <b>{doneCount(r)}/{r.criteria.length}</b> điều kiện · tiến độ <b>{progress}%</b>
          {r.criteria.some(c => c.status !== 'done') && (
            <ul className="mt-2 list-disc pl-5 text-xs text-gray-500">
              {r.criteria.filter(c => c.status !== 'done').map(c => <li key={c.id}>{c.title}: {CRITERION_STATUS[c.status].label.toLowerCase()}</li>)}
            </ul>
          )}
        </div>
        <Form form={evalForm} layout="vertical" onFinish={submitEvaluation} initialValues={{ result: ready ? 'promoted' : 'continue' }} preserve={false}>
          <Form.Item name="result" label="Kết quả">
            <Radio.Group className="flex flex-col gap-2">
              <Radio value="promoted">Đủ điều kiện — thăng tiến lên <b>{target}</b></Radio>
              <Radio value="continue">Chưa đủ — tiếp tục theo dõi</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.result !== b.result}>
            {({ getFieldValue }) => getFieldValue('result') === 'promoted' && !ready && (
              <Alert type="warning" showIcon className="!mb-3" message="Vẫn còn điều kiện chưa đạt. Hãy ghi rõ lý do xét thăng tiến trong nhận xét." />
            )}
          </Form.Item>
          <Form.Item name="note" label="Nhận xét" rules={[{ required: true, whitespace: true, message: 'Nhập nhận xét đánh giá' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="Nhận xét về năng lực, điểm cần cải thiện..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
